/**
 * PATH: packages/ui/src/qa/QaPhotoDisplayControls.tsx
 * FILE_ID: FE-QA-PHOTO-DISPLAY-CONTROLS-001
 * PURPOSE: One Size popover and one Fit/Fill toggle for hosted QA galleries.
 * LANDMINE: Portal menu only. Do not add absolute/fixed chrome on the toolbar.
 */

import { useCallback, type MouseEvent } from 'react'

import { ContextMenu, useContextMenu, type ContextMenuItem } from '../contextMenu'
import { useQaPhotoPrefs } from './qaPhotoPrefs'
import { QA_PHOTO_SIZE_LABEL, QA_PHOTO_SIZES } from './qaPhotoView'

export function QaPhotoDisplayControls() {
  const prefs = useQaPhotoPrefs()
  const menu = useContextMenu()

  const open = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    menu.openAt({
      clientX: rect.left,
      clientY: rect.bottom,
      currentTarget: event.currentTarget,
      preventDefault: () => event.preventDefault(),
    })
  }, [menu])

  const items: ContextMenuItem[] = [
    {
      id: 'header',
      kind: 'header',
      label: `Photo size · ${prefs.selectedCount > 0 ? `${prefs.selectedCount} selected` : 'all photos'}`,
    },
    ...QA_PHOTO_SIZES.map((size) => ({
      id: String(size),
      label: QA_PHOTO_SIZE_LABEL[size],
      hint: prefs.activeView.size === size ? 'Current' : undefined,
      onSelect: () => {
        menu.close()
        prefs.setSize(size)
      },
    })),
    ...(prefs.hasOverrides
      ? [
          { id: 'sep', kind: 'separator' as const },
          {
            id: 'reset',
            label: 'Reset photos to default',
            onSelect: () => {
              menu.close()
              prefs.resetOverrides()
            },
          },
        ]
      : []),
  ]

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={open}
        data-testid="qa-photo-size"
        className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2 text-[11px]"
      >
        {QA_PHOTO_SIZE_LABEL[prefs.activeView.size]}
      </button>
      {menu.isOpen && <ContextMenu {...menu.props} items={items} width={200} />}
      <button
        type="button"
        data-testid="qa-photo-fit"
        aria-pressed={prefs.activeView.fit}
        onClick={() => prefs.setFit(!prefs.activeView.fit)}
        className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2 text-[11px]"
      >
        {prefs.activeView.fit ? 'Fit' : 'Fill'}
      </button>
    </div>
  )
}
